import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createApiClient } from '@shared/api/client/api-client';
import { createAuthApi } from '@shared/api/lib/auth-api';
import {
  isPasswordPolicyRule,
  mapAuthResponseDtoToEntity,
  type AuthAction,
  type PasswordPolicyRule,
} from '@entities/auth';
import { useAuth } from '@entities/auth';
import { applyAuthResponse } from '@entities/auth';
import {
  trackAuthRegisterAttempted,
  trackAuthRegisterFailed,
  trackAuthRegisterSuccess,
} from '@shared/lib/analytics';
import { readApiError } from '@shared/api/errors/read-api-error';
import { isApiError } from '@shared/api/errors/errors';

import { env } from '@shared/config/env';

interface UseRegisterResult {
  isLoading: boolean;
  error: string | null;
  register: (data: {
    email: string;
    password: string;
    repeatPassword: string;
    referralCode?: string;
    telegram?: string | null;
    whatsapp?: string | null;
  }) => Promise<{
    success: boolean;
    action?: AuthAction;
    error?: string;
    policyRules?: PasswordPolicyRule[];
    fieldErrors?: Record<string, string>;
  }>;
}

const isUserExistsError = (err: unknown): boolean => {
  if (!isApiError(err) || err.status !== 400 || typeof err.data !== 'object' || err.data === null) {
    return false;
  }
  return (err.data as { message?: unknown }).message === 'User exist';
};

export function useRegister(): UseRegisterResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('auth');
  const { login: authLogin } = useAuth();

  const register = async (data: {
    email: string;
    password: string;
    repeatPassword: string;
    referralCode?: string;
    telegram?: string | null;
    whatsapp?: string | null;
  }): Promise<{
    success: boolean;
    action?: AuthAction;
    error?: string;
    policyRules?: PasswordPolicyRule[];
    fieldErrors?: Record<string, string>;
  }> => {
    setIsLoading(true);
    setError(null);
    trackAuthRegisterAttempted();

    try {
      const apiClient = createApiClient({ baseURL: env.API_BASE_URL });
      const authApi = createAuthApi(apiClient);

      const response = await authApi.register(data);
      const authResponse = mapAuthResponseDtoToEntity(response);
      const { action } = await applyAuthResponse(authResponse, authLogin);
      trackAuthRegisterSuccess();

      return {
        success: true,
        action,
      };
    } catch (err: unknown) {
      const parsed = readApiError(err);
      trackAuthRegisterFailed('register_failed');

      if (isUserExistsError(err)) {
        return { success: false, fieldErrors: { email: 'errors.emailAlreadyRegistered' } };
      }

      if (parsed?.code === 'PASSWORD_POLICY') {
        return { success: false, policyRules: parsed.rules.filter(isPasswordPolicyRule) };
      }

      if (parsed && Object.keys(parsed.fieldErrors).length > 0) {
        return { success: false, fieldErrors: parsed.fieldErrors };
      }

      const errorMessage = t('errors.registrationFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, error, register };
}
